import React, { FC, useContext, useState } from 'react';
import { useQuery } from '@apollo/client';
import { Box, IconButton, Paper, Tab, Tabs, Tooltip, Typography } from '@material-ui/core';
import { Settings as SettingsIcon } from '@material-ui/icons';
import TitleAction from '@/se/components/TitleAction';
import { Scope } from '@/contexts/ScopeContext';
import {
  DATA_EXPORT_DEFINITIONS,
  DATA_EXPORT_DELIVERY_CHANNELS,
  DataExportDefinitionsData,
  DataExportDefinitionsVars,
  DataExportDeliveryChannelsData,
  DataExportDeliveryChannelsVars,
} from '@/graph/data-export-admin';
import { ChannelsDialog } from './channels-dialog';
import { ManualTab } from './manual-tab';
import { ScheduledTab } from './scheduled-tab';
import { TabKey, useSharedStyles } from './shared';

/**
 * Org-scoped data-export admin page.
 *
 * Two top-level tabs: **Manual** (operator-triggered exports + batch
 * history with per-batch download/redeliver/retry and per-delivery
 * redelivery) and **Scheduled** (configured definitions with
 * per-schedule channel attach/detach + history + last-run failure
 * banner).
 *
 * Channels are a configuration concern, not a workflow — they shape
 * WHERE the runs from the tabs deliver to but aren't a day-to-day
 * operator surface. They live behind a settings cog next to the page
 * title; click it to open the channels dialog.
 *
 * Org comes from the Scope context — `/su/<orgId>/data-export` for
 * super-admins, `/data-export` for a regular admin whose token binds
 * them to one org. There is no org switcher on the page.
 *
 * Audience: super-admin (sidebar-gated in `admin/Homepage.jsx`). The
 * page itself contains nothing super-admin-specific; opening it to
 * regular admins is a sidebar-only flip.
 *
 * Cross-org operations (firing the hourly schedule tick) deliberately
 * do not live here; they belong on a future cross-org admin surface.
 */
const DataExportAdminPage: FC = () => {
  const classes = useSharedStyles();
  const scope = useContext(Scope);
  const organizationId: number | undefined = scope?.hospital?.id;
  const [tab, setTab] = useState<TabKey>('manual');
  const [channelsDialogOpen, setChannelsDialogOpen] = useState(false);
  // Advanced-mode flag, toggled by Shift+D from anywhere on the page
  // outside form inputs. Gates the channel-configuration cog, the
  // Dry-run checkbox, and production diagnostics expansions. These
  // are infrequently-needed power-user controls;
  // hiding them by default keeps the everyday operator surface
  // focused on the "trigger an export" action. Default off — the
  // operator opts in.
  const [advancedMode, setAdvancedMode] = useState(false);
  React.useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!e.shiftKey || e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.key.toLowerCase() !== 'd') return;
      // Skip if the operator is typing in a form — Shift+D in a field
      // is a real letter D, not the toggle.
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || target?.isContentEditable) return;
      e.preventDefault();
      setAdvancedMode(prev => !prev);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);
  // Toggling advanced mode off auto-closes the channels dialog —
  // the dialog IS the advanced surface for channels, and leaving it
  // open after the operator hid the entry point feels inconsistent.
  React.useEffect(() => {
    if (!advancedMode) setChannelsDialogOpen(false);
  }, [advancedMode]);

  // `organizationId` is passed as a variable so Apollo keys the cache
  // per org — without it, navigating between orgs in a super-admin
  // session would render the previous org's data from cache. `skip`
  // only guards the fatal-throw path below: if we ever do render with
  // no org, the hook still has to run unconditionally to satisfy
  // React's rules-of-hooks.
  const {
    data: definitionsData,
    loading: definitionsLoading,
    error: definitionsError,
    refetch: refetchDefinitions,
  } = useQuery<DataExportDefinitionsData, DataExportDefinitionsVars>(DATA_EXPORT_DEFINITIONS, {
    variables: organizationId != null ? { organizationId } : undefined,
    skip: organizationId == null,
    fetchPolicy: 'cache-and-network',
  });

  const {
    data: channelsData,
    loading: channelsLoading,
    error: channelsError,
    refetch: refetchChannelsQuery,
  } = useQuery<DataExportDeliveryChannelsData, DataExportDeliveryChannelsVars>(DATA_EXPORT_DELIVERY_CHANNELS, {
    variables: organizationId != null ? { organizationId } : undefined,
    skip: organizationId == null,
    fetchPolicy: 'cache-and-network',
  });

  // No-org is a fatal invariant violation, not a user-facing state.
  // The parent ScopeProvider blocks render until scope resolves, and
  // this page is only mounted under the org-scoped admin shell. If we
  // got here with no org, something is wrong with routing or the
  // scope query — the page has no org switcher for the user to act
  // on, so a polite "select an organization" banner would just
  // confuse them. Throw so the app-level error boundary surfaces it.
  if (organizationId == null) {
    throw new Error('Data Export: no organization in scope (routing or scope-query bug).');
  }

  const definitions = definitionsData?.dataExportDefinitions ?? [];
  const channels = channelsData?.dataExportDeliveryChannels ?? [];

  const refreshDefinitions = () => {
    refetchDefinitions().catch(() => undefined);
  };
  const refreshChannels = () => {
    refetchChannelsQuery().catch(() => undefined);
  };

  // Definitions error is shown inline so transient failures don't
  // blank the page when a stale cache is still rendering useful state.
  const definitionsBanner =
    definitionsError && definitions.length === 0 ? (
      <Paper className={classes.emptyBanner} elevation={1}>
        <Typography variant="body2" color="error">
          Could not load exports: {definitionsError.message}
        </Typography>
      </Paper>
    ) : definitionsLoading && definitions.length === 0 && channelsLoading && channels.length === 0 ? (
      <Paper className={classes.emptyBanner} elevation={1}>
        <Typography variant="body2" color="textSecondary">
          Loading…
        </Typography>
      </Paper>
    ) : null;

  return (
    <Box className={classes.root}>
      {/* `Typography variant="h1"` directly, matching the Analytics page
          (and Patients via CollectionList's DefaultTitle). The H2-based
          DefaultTitle from `@/se/components/entity/EntityRouter` is
          smaller and was producing a visibly different title scale than
          the rest of the admin shell. */}
      <TitleAction Title={() => <Typography variant="h1">Data Export</Typography>}>
        {/* Channel-configuration cog hidden until Shift+D. Channels
            are seeded out-of-band (SQL templates) for the common
            case; surfacing the configure shortcut only to power
            users keeps the title bar clean. */}
        {advancedMode && (
          <Tooltip title="Delivery channels (Shift+D to hide)">
            <IconButton size="small" onClick={() => setChannelsDialogOpen(true)} aria-label="open-channels-dialog">
              <SettingsIcon />
            </IconButton>
          </Tooltip>
        )}
      </TitleAction>
      <Tabs
        value={tab}
        onChange={(_, next: TabKey) => setTab(next)}
        indicatorColor="primary"
        textColor="primary"
        className={classes.tabs}
      >
        <Tab value="manual" label="Manual" />
        <Tab value="scheduled" label="Scheduled" />
      </Tabs>
      {definitionsBanner}
      {definitionsError && definitions.length > 0 && (
        <Paper className={classes.emptyBanner} elevation={1}>
          <Typography variant="body2" color="error">
            Could not refresh exports: {definitionsError.message}
          </Typography>
        </Paper>
      )}
      {channelsError && (
        <Paper className={classes.emptyBanner} elevation={1}>
          <Typography variant="body2" color="error">
            Could not refresh channels: {channelsError.message}
          </Typography>
        </Paper>
      )}
      {tab === 'manual' ? (
        <ManualTab
          organizationId={organizationId}
          definitions={definitions}
          channels={channels}
          advancedMode={advancedMode}
        />
      ) : (
        <ScheduledTab
          organizationId={organizationId}
          definitions={definitions}
          channels={channels}
          advancedMode={advancedMode}
          refetchDefinitions={refreshDefinitions}
        />
      )}
      <ChannelsDialog
        open={channelsDialogOpen}
        onClose={() => setChannelsDialogOpen(false)}
        organizationId={organizationId}
        channels={channels}
        channelsLoading={channelsLoading}
        refetchChannels={refreshChannels}
      />
    </Box>
  );
};

export default DataExportAdminPage;
